import { useCallback, useMemo, useState } from 'react';

import type { ApolloCache } from '@apollo/client';
import type { ZonedDateTime } from '@js-joda/core';
import type { OdProtosWebData_ParticipantInput } from '__generated__/globalTypes';
import { OdProtosWebData_ParticipantRole } from '__generated__/globalTypes';
import type { MutateResult, UseMutationResult } from 'platform/apollo';
import { useMutation } from 'platform/apollo';
import { useAuthAuthenticated } from 'platform/components/AuthContext';
import type {
  DeleteParticipantMutation,
  ResendParticipantInviteMutation,
  UpsertParticipantMutation,
} from 'sellDirect/graphql/__generated__/useParticipants';
import {
  DeleteParticipantDocument,
  ResendParticipantInviteDocument,
  UpsertParticipantDocument,
} from 'sellDirect/graphql/__generated__/useParticipants';
import type { ParticipantFragment } from 'sellDirect/graphql/fragments/__generated__/ParticipantFragment';
import { systemZonedDateTimeFromString } from 'shared/helpers/datetime';

import type { SellerTransaction } from './useSellerTransaction';

// Keep this in sync with MAX_SIGNATORIES in Web Participant model.
// https://github.com/opendoor-labs/web/blob/4d3fe59573f6adadb758398b45a90b809261c511/app/models/participant.rb#L7
const MAX_SIGNATORIES = 2;

const SELLER_TEAM_CONTEXT = { linkContext: { team: 'seller' } } as const;

export interface Participant extends Omit<ParticipantFragment, 'createdAt'> {
  createdAt: ZonedDateTime; // generated GraphQL type is any, so Omit it and replace with ZonedDateTime

  isInitiator: boolean;
  isSignatory: boolean;
  isCurrentUser: boolean;
}

export interface UseParticipantsResult {
  /** Participants. Undefined if not yet known. */
  participants: Participant[] | undefined;
  /** If current user is initiator of lead. Undefined if not yet known. */
  currentUserIsInitiator: boolean | undefined;
  /** If current user is a participant of a lead. An initiator is also a participant. Undefined if not yet known. */
  currentUserIsParticipant: boolean | undefined;
  /** If another signatory can be added. Max signatories is 2. Undefined if not yet known. */
  canAddSignatory: boolean | undefined;
  /** Set when an email-change upsert causes the API to create a new participant instead of updating in-place. */
  lastReplacedParticipant: { oldId: string; newId: string } | undefined;
  upsertParticipant: (
    participant: OdProtosWebData_ParticipantInput,
  ) => Promise<MutateResult<UpsertParticipantMutation>>;
  upsertResult: UseMutationResult<UpsertParticipantMutation>;
  deleteParticipant: (participantId: string) => Promise<MutateResult<DeleteParticipantMutation>>;
  deleteResult: UseMutationResult<DeleteParticipantMutation>;
  resendParticipantInvite: (
    participantId: string,
  ) => Promise<MutateResult<ResendParticipantInviteMutation>>;
  resendParticipantInviteResult: UseMutationResult<ResendParticipantInviteMutation>;
}

const transactionTypeHasParticipants = (transaction: SellerTransaction) => {
  const typeName = transaction.__typename;
  return (
    typeName === 'QualifiedTransaction' ||
    typeName === 'InContractTransaction' ||
    typeName === 'WithdrawnTransaction'
  );
};

/**
 * Manages participant state and provides mutation functions.
 */
export const useParticipants = (
  sellerInputUUID: string,
  sellerTransaction: SellerTransaction | null,
): UseParticipantsResult => {
  const { user: currentHuman } = useAuthAuthenticated();
  const [lastReplacedParticipant, setLastReplacedParticipant] = useState<
    { oldId: string; newId: string } | undefined
  >();

  const updateParticipantsCache = (
    cache: ApolloCache,
    newParticipants: ParticipantFragment[] | null | undefined,
  ) => {
    if (!newParticipants || !sellerTransaction) return;
    cache.modify({
      id: cache.identify(sellerTransaction),
      fields: {
        sellerParticipants: () =>
          newParticipants.filter((participant) =>
            participant.roles.includes(OdProtosWebData_ParticipantRole.SELLER_PARTICIPANT),
          ),
      },
    });
  };

  const [upsertParticipantGql, upsertResult] = useMutation(UpsertParticipantDocument, {
    context: SELLER_TEAM_CONTEXT,
    update(cache, { data }) {
      updateParticipantsCache(
        cache,
        data?.sellDirect.upsertParticipant?.participants?.participants,
      );
    },
  });

  const [deleteParticipantGql, deleteResult] = useMutation(DeleteParticipantDocument, {
    context: SELLER_TEAM_CONTEXT,
    update(cache, { data }) {
      updateParticipantsCache(
        cache,
        data?.sellDirect.deleteParticipant?.participants?.participants,
      );
    },
  });

  const [resendParticipantInviteGql, resendParticipantInviteResult] = useMutation(
    ResendParticipantInviteDocument,
    {
      fetchPolicy: 'no-cache',
      context: SELLER_TEAM_CONTEXT,
    },
  );

  const upsertParticipant = useCallback(
    (participant: OdProtosWebData_ParticipantInput) =>
      upsertParticipantGql({
        variables: { sellerInputUUID, participants: [participant] },
        onCompleted: (data) => {
          // When a participant's email changes, the BE deletes the old participant and creates a new
          // one. Map the old ID to the new ID so the open dialog follows the replacement.
          if (!participant.email || !participant.id) return;
          const responseParticipants =
            data.sellDirect.upsertParticipant?.participants?.participants ?? [];
          const oldStillExists = responseParticipants.some((p) => p.id === participant.id);
          if (!oldStillExists) {
            const replacement = responseParticipants.find((p) =>
              emailsMatch(p.email, participant.email ?? null),
            );
            if (replacement) {
              setLastReplacedParticipant({ oldId: participant.id, newId: replacement.id });
            }
          }
        },
      }),
    [sellerInputUUID, upsertParticipantGql],
  );

  const deleteParticipant = useCallback(
    (participantId: string) =>
      deleteParticipantGql({
        variables: {
          sellerInputUUID,
          participantId,
        },
      }),
    [deleteParticipantGql, sellerInputUUID],
  );

  const resendParticipantInvite = useCallback(
    (participantId: string) =>
      resendParticipantInviteGql({
        variables: {
          sellerInputUUID,
          participantId,
        },
      }),
    [resendParticipantInviteGql, sellerInputUUID],
  );

  const participants = useMemo(() => {
    if (
      !sellerTransaction ||
      !transactionTypeHasParticipants(sellerTransaction) ||
      !sellerTransaction.sellerParticipants
    )
      return undefined;

    return (
      sellerTransaction.sellerParticipants
        // Add computed Participant fields.
        .map<Participant>(({ createdAt, ...p }) => {
          return {
            ...p,
            createdAt: systemZonedDateTimeFromString(createdAt),
            isInitiator: p.roles.includes(OdProtosWebData_ParticipantRole.INITIATOR),
            isSignatory: p.roles.includes(OdProtosWebData_ParticipantRole.SIGNATORY),
            isCurrentUser: emailsMatch(p.email, currentHuman?.email),
          };
        })
    );
  }, [currentHuman, sellerTransaction]);

  const { currentUserIsInitiator, currentUserIsParticipant } = useMemo(() => {
    if (!participants) {
      return {
        currentUserIsInitiator: undefined,
        currentUserIsParticipant: undefined,
      };
    }

    const curParticipant = participants.find((p) => emailsMatch(p.email, currentHuman?.email));
    if (!curParticipant) {
      return {
        currentUserIsInitiator: false,
        currentUserIsParticipant: false,
      };
    }

    return {
      currentUserIsInitiator: curParticipant.isInitiator,
      currentUserIsParticipant: true,
    };
  }, [currentHuman, participants]);

  const canAddSignatory = useMemo(() => {
    if (!participants) {
      return undefined;
    }

    const signatories = participants.filter((p) => p.isSignatory);
    return signatories.length < MAX_SIGNATORIES;
  }, [participants]);

  return useMemo<UseParticipantsResult>(
    () => ({
      participants,
      canAddSignatory,
      currentUserIsInitiator,
      currentUserIsParticipant,
      lastReplacedParticipant,
      upsertParticipant,
      upsertResult,
      deleteParticipant,
      deleteResult,
      resendParticipantInvite,
      resendParticipantInviteResult,
    }),
    [
      participants,
      canAddSignatory,
      currentUserIsInitiator,
      currentUserIsParticipant,
      lastReplacedParticipant,
      upsertParticipant,
      upsertResult,
      deleteParticipant,
      deleteResult,
      resendParticipantInvite,
      resendParticipantInviteResult,
    ],
  );
};

/** Returns true if both strings are present and compares them case insensitively. */
const emailsMatch = (s1: string | null | undefined, s2: string | null | undefined): boolean => {
  if (!s1 || !s2) {
    return false;
  }
  return s1.toLowerCase() === s2.toLowerCase();
};
